"use client"
import { useState, useEffect } from 'react';
import { DashboardHeader } from '@/components/dashboard-header';
import { DashboardShell } from '@/components/dashboard-shell';
import { Button } from '@/components/ui/button';
import { Patient } from '@/types';
import { supabase } from '@/utils/supabase/client';
import { useOrganization } from '@/contexts/organization-context';
import { PatientList } from '@/components/patient-list';
import { PatientFormModal } from '@/components/patient-form-modal';
import { useToast } from "@/components/ui/use-toast"
import { PlusCircle, Search } from 'lucide-react';

export default function PatientsPage() {
  const { organization } = useOrganization();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    const fetchPatients = async () => {
      if (!organization) return;
      setIsLoading(true);
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('organization_id', organization.id);
      
      if (error) {
        toast({
          title: "Error fetching patients",
          description: error.message,
          variant: "destructive",
        })
      } else {
        setPatients(data);
      }
      setIsLoading(false);
    };

    fetchPatients();
  }, [organization, toast]);

  const handleAddPatient = async (patient: Omit<Patient, 'id' | 'created_at' | 'mr_number'>) => {
    const { data, error } = await supabase.from('patients').insert([patient]).select('*').single();
    if (!error && data) {
      setPatients([...patients, data]);
      setIsModalOpen(false);
      toast({ title: "Patient added", description: `${patient.name} has been added.` });
    } else {
      toast({ title: "Error", description: error?.message || "Failed to add patient", variant: "destructive" });
    }
  };

  const handleEditPatient = (patient: Patient) => {
    setEditingPatient(patient);
    setIsModalOpen(true);
  };

  const handleUpdatePatient = async (patient: Patient) => {
    const { data, error } = await supabase.from('patients').update(patient).eq('id', patient.id).select('*').single();
    if (!error && data) {
      setPatients(patients.map(p => p.id === data.id ? data : p));
      setIsModalOpen(false);
      setEditingPatient(null);
      toast({ title: "Patient updated", description: `${patient.name}'s information has been updated.` });
    } else {
      toast({ title: "Error", description: error?.message || "Failed to update patient", variant: "destructive" });
    }
  };

  const handleDeletePatient = async (id: string) => {
    const { error } = await supabase.from('patients').delete().eq('id', id);
    if (!error) {
      setPatients(patients.filter(p => p.id !== id));
      toast({ title: "Patient removed", description: `Patient has been removed.` });
    } else {
      toast({ title: "Error", description: error?.message || "Failed to delete patient", variant: "destructive" });
    }
  };

  const handleModalClose = () => {
    setEditingPatient(null);
    setIsModalOpen(false);
  };

  const filteredPatients = patients.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.phone.toLowerCase().includes(search.toLowerCase()) ||
      p.mr_number.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="md:ml-64">
      <DashboardShell>
        <DashboardHeader heading="Patients" text="Manage your clinic's patients">
            <div className="flex gap-2 w-full max-w-md">
                <div className="relative flex-1">
                    <input
                        type="text"
                        placeholder="Search by name, phone, or MR number..."
                        className="w-full border rounded px-3 py-2 pl-9 text-sm"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                </div>
                <Button onClick={() => { setEditingPatient(null); setIsModalOpen(true); }}>
                    <PlusCircle className="mr-2 h-4 w-4" />
                    Add Patient
                </Button>
            </div>
        </DashboardHeader>
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <p>Loading patients...</p>
          </div>
        ) : (
          <PatientList 
            patients={filteredPatients} 
            onEdit={handleEditPatient}
            onDelete={handleDeletePatient}
          />
        )}
        <PatientFormModal
          isOpen={isModalOpen} 
          onClose={handleModalClose}
          onPatientCreated={handleAddPatient}
          onPatientUpdated={handleUpdatePatient}
          patientToEdit={editingPatient}
        />
      </DashboardShell>
    </div>
  );
} 